import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MAX_SCHEDULE_DAYS = 30;

/**
 * State + API logic for the private newsletter admin panel.
 *
 * The hook owns:
 *   - login / stats fetching
 *   - composer fields (subject, body, scheduledAt)
 *   - send-test / broadcast / schedule / cancel
 *
 * The component just renders the returned props.
 */
export const useAdminNewsletter = (initialBody = "") => {
  const [password, setPassword] = useState("");
  const [loggedIn, setLoggedIn] = useState(false);
  const [stats, setStats] = useState(null);

  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState(initialBody);
  const [scheduledAt, setScheduledAt] = useState("");
  const [preview, setPreview] = useState(false);

  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/admin/stats`, { password });
      setStats(data);
      setLoggedIn(true);
      toast.success("Bienvenida, Marta");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Contraseña incorrecta");
    } finally {
      setLoading(false);
    }
  };

  const refreshStats = async () => {
    try {
      const { data } = await axios.post(`${API}/newsletter/admin/stats`, { password });
      setStats(data);
    } catch (err) {
      console.error("[admin-newsletter] stats fetch failed:", err);
    }
  };

  const validate = () => {
    if (!subject.trim() || subject.trim().length < 3) {
      toast.error("Falta el asunto (mínimo 3 caracteres)");
      return false;
    }
    if (!bodyHtml.trim() || bodyHtml.trim().length < 20) {
      toast.error("Falta el cuerpo de la newsletter (mínimo 20 caracteres)");
      return false;
    }
    return true;
  };

  const sendTest = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/admin/broadcast`, {
        password,
        subject,
        body_html: bodyHtml,
        test_mode: true,
      });
      toast.success(`Test enviado a ${data.recipients?.[0] || "tu email"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar test");
    } finally {
      setLoading(false);
    }
  };

  const resolveSchedule = () => {
    if (!scheduledAt) return { iso: null, confirmMsg: null, ok: true };
    const localDate = new Date(scheduledAt);
    if (isNaN(localDate.getTime())) {
      toast.error("Fecha/hora inválida");
      return { ok: false };
    }
    if (localDate.getTime() < Date.now() + 60_000) {
      toast.error("La fecha programada debe ser al menos 1 minuto en el futuro");
      return { ok: false };
    }
    const maxDate = new Date(Date.now() + MAX_SCHEDULE_DAYS * 24 * 60 * 60 * 1000);
    if (localDate > maxDate) {
      toast.error(`La fecha no puede ser más de ${MAX_SCHEDULE_DAYS} días en el futuro (límite de Resend)`);
      return { ok: false };
    }
    const friendlyDate = localDate.toLocaleString("es-ES", {
      dateStyle: "full",
      timeStyle: "short",
    });
    return {
      ok: true,
      iso: localDate.toISOString(),
      confirmMsg: `¿Programar esta newsletter para ${friendlyDate}? Se enviará automáticamente a ${stats?.subscribers_total || 0} suscriptoras.`,
    };
  };

  const sendBroadcast = async () => {
    if (!validate()) return;
    const schedule = resolveSchedule();
    if (!schedule.ok) return;

    const confirmMsg =
      schedule.confirmMsg ||
      `¿Enviar AHORA esta newsletter a ${stats?.subscribers_total || 0} suscriptoras? Esta acción no se puede deshacer.`;
    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/admin/broadcast`, {
        password,
        subject,
        body_html: bodyHtml,
        test_mode: false,
        scheduled_at: schedule.iso,
      });
      if (data.mode === "scheduled") {
        toast.success(`Programada · ${data.sent} emails en cola en Resend`);
      } else {
        toast.success(`Enviada · ${data.sent} OK · ${data.failed} fallidos`);
      }
      setSubject("");
      setBodyHtml(initialBody);
      setScheduledAt("");
      await refreshStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar");
    } finally {
      setLoading(false);
    }
  };

  const cancelScheduled = async (broadcastId, broadcastSubject) => {
    if (!window.confirm(`¿Cancelar el envío programado "${broadcastSubject}"? Se cancelarán todos los emails en cola.`)) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/admin/cancel-scheduled`, {
        password,
        broadcast_id: broadcastId,
      });
      toast.success(`Cancelado · ${data.cancelled_emails}/${data.total} emails cancelados`);
      await refreshStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cancelar");
    } finally {
      setLoading(false);
    }
  };

  const sendWeeklySummary = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/admin/send-weekly-summary`, { password });
      if (data.sent) {
        toast.success("Resumen enviado a tu email. Revisa la bandeja.");
      } else {
        toast.error(data.skipped === "no-recipient" ? "Falta NOTIFICATION_EMAIL en .env" : "No se pudo enviar");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar el resumen");
    } finally {
      setLoading(false);
    }
  };

  return {
    // auth
    password, setPassword, loggedIn, login,
    // data
    stats,
    // composer
    subject, setSubject,
    bodyHtml, setBodyHtml,
    scheduledAt, setScheduledAt,
    preview, setPreview,
    // actions
    loading, sendTest, sendBroadcast, cancelScheduled, sendWeeklySummary,
  };
};

export default useAdminNewsletter;
